import React, { useContext, useRef } from 'react';
import { createForm } from 'final-form';
import { Form, FormSpy } from 'react-final-form';
import { Grid } from '@material-ui/core';
import { ReferenceArrayInput } from 'react-admin';
import GridInput from '../../../core/components/inputs/GridInput';
import { CreationFundContext } from './CreationFundContext';
import FundInvestorApi from '../../../core/apis/FundInvestorApi';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { isEmpty } from 'lodash';
import { Alert } from '@material-ui/lab';
import { STEP } from './utils';

const validateInvestors = value => {
  if (Array.isArray(value) && value.length < 1) {
    return 'At least one Investor must be selected';
  }
  return undefined;
};

const ReferenceArrayInputProps = {
  label: '',
  optionTextLabel: 'Investor Name',
  optionValueLabel: 'Investor ID',
  additionalColumns: [
    { label: 'Status', name: 'status', isStatusColumn: true }
  ],
  resource: 'investors',
  reference: 'investors',
  source: 'investors'
};

const AddRelatedInvestors = props => {
  const {
    setIsNextAvailable,
    setIsSpinnerOnNextButton,
    onSubmitEnd,
    setCurrentAuthorizations,
    hasDeleteFundInvestorRelationship,
    setListOfSteps,
    listOfSteps
  } = props;
  const notifyServerError = useNotifyServerError();
  const { fundContext, setFundContext } = useContext(CreationFundContext);
  const initialValues = isEmpty(fundContext.investorIds)
    ? { investors: [] }
    : { investors: fundContext.investorIds };

  function filterFundInvestorsStates(fi) {
    setFundContext(prev => ({
      ...prev,
      fundInvestors: [
        ...prev.fundInvestors.filter(elem => elem.investorId !== fi.investorId)
      ],
      investorIds: [...prev.investorIds.filter(id => id !== fi.investorId)]
    }));

    // We remove any permission that contains the investor removed.
    setCurrentAuthorizations(prev => [
      ...prev.filter(
        authorization => authorization.investorId !== fi.investorId
      )
    ]);
  }

  const onSubmit = async values => {
    setIsSpinnerOnNextButton(true);
    const fundId = fundContext.fund.id;
    let payload = [];

    values.investors.forEach(investorId => {
      payload.push({
        fundId,
        investorId: investorId
      });
    });

    // First submission, we create fundInvestors
    if (isEmpty(fundContext.fundInvestors)) {
      setFundContext(prev => ({ ...prev, investorIds: values.investors }));
      try {
        await FundInvestorApi.create(payload).then(res => {
          if (!isEmpty(res)) {
            setIsSpinnerOnNextButton(false);
            setFundContext(prev => ({ ...prev, fundInvestors: res }));
            const updatedListOfStep = listOfSteps.map(step => {
              if (step.name === STEP.addRelatedInvestors) {
                return { ...step, completed: true };
              }
              return step;
            });
            setListOfSteps(updatedListOfStep);
            onSubmitEnd();
          }
        });
      } catch (e) {
        notifyServerError(e, undefined);
        setIsSpinnerOnNextButton(false);
      }
    } else {
      // If not the first submission, we check if there are any fund-investors to delete/add
      const fundInvestorToDelete = fundContext.fundInvestors.reduce(
        (acc, curr) => {
          if (
            !payload.some(element => element.investorId === curr.investorId)
          ) {
            acc = [...acc, curr];
          }
          return acc;
        },
        []
      );

      const fundInvestorToAdd = payload.reduce((acc, curr) => {
        if (
          !fundContext.fundInvestors.some(
            element => element.investorId === curr.investorId
          )
        ) {
          acc = [...acc, curr];
        }
        return acc;
      }, []);

      if (
        fundInvestorToDelete.length > 0 &&
        fundInvestorToAdd.length === 0 &&
        hasDeleteFundInvestorRelationship
      ) {
        await FundInvestorApi.delete(fundInvestorToDelete)
          .then(() => {
            fundInvestorToDelete.forEach(fi => filterFundInvestorsStates(fi));
            setIsSpinnerOnNextButton(false);
            onSubmitEnd();
          })
          .catch(e => notifyServerError(e));
      } else if (
        fundInvestorToDelete.length === 0 &&
        fundInvestorToAdd.length > 0
      ) {
        await FundInvestorApi.create(fundInvestorToAdd)
          .then(res => {
            setFundContext(prev => ({
              ...prev,
              fundInvestors: [...prev['fundInvestors'], ...res],
              investorIds: values.investors
            }));
            setIsSpinnerOnNextButton(false);
            onSubmitEnd();
          })
          .catch(e => notifyServerError(e));
      } else if (
        fundInvestorToDelete.length > 0 &&
        fundInvestorToAdd.length > 0
      ) {
        if (hasDeleteFundInvestorRelationship) {
          await FundInvestorApi.delete(fundInvestorToDelete).then(() => {
            fundInvestorToDelete.forEach(fi => filterFundInvestorsStates(fi));
          });
        }

        await FundInvestorApi.create(fundInvestorToAdd).then(res => {
          setFundContext(prev => ({
            ...prev,
            fundInvestors: [...prev.fundInvestors, ...res]
          }));
        });
        fundInvestorToAdd.forEach(fi =>
          setFundContext(prev => ({
            ...prev,
            investorIds: [...prev.investorIds, fi.investorId]
          }))
        );
        setIsSpinnerOnNextButton(false);
        onSubmitEnd();
      } else {
        setIsSpinnerOnNextButton(false);
        onSubmitEnd();
      }
    }
  };

  const formRef = useRef(
    createForm({
      onSubmit: onSubmit,
      initialValues
    })
  );

  const displayWarningMessage = () => {
    if (
      !isEmpty(fundContext.fundInvestors) &&
      !hasDeleteFundInvestorRelationship
    ) {
      return (
        <Alert severity="warning">
          You don't have the permission to remove Related Investors. If
          unchecked, they will not be removed.
        </Alert>
      );
    }
    return null;
  };

  return (
    <>
      <Form form={formRef.current}>
        {({ handleSubmit }) => (
          <>
            <FormSpy
              subscription={{ submitting: true, values: true }}
              onChange={formStateProps => {
                setIsNextAvailable(
                  !formStateProps.submitting &&
                    formStateProps.values.investors.length > 0
                );
              }}
            />
            <form onSubmit={handleSubmit} id="formAddRelatedInvestors">
              <Grid container spacing={1}>
                <ReferenceArrayInput
                  perPage={-1}
                  sort={{ field: 'name', order: 'ASC' }}
                  validate={validateInvestors}
                  filter={{ orphanVisibility: 'ALL_VISIBLE' }}
                  {...ReferenceArrayInputProps}
                >
                  <GridInput height={'calc(100vh - 412px)'} />
                </ReferenceArrayInput>
              </Grid>
            </form>
          </>
        )}
      </Form>
      {displayWarningMessage()}
    </>
  );
};

export default AddRelatedInvestors;
